import { SessionDetails } from "./SessionDetails";
import { useSessionPopover } from "./use-session-popover";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { platformName, selectedPlatforms } from "../../lib/stream-platform";
import type { CalendarEvent } from "./schedule-events";
import { platformTones } from "./platform-tone";

export const HOUR_HEIGHT = 20;

export function CalendarEventCard({ event, tabIndex = 0 }: { event: CalendarEvent; tabIndex?: number }) {
  const { open, onOpenChange, show, hideSoon } = useSessionPopover();
  const { t } = useTranslation();
  const reference = event.reference;
  const referencePrefix = reference ? `${reference.name} · ` : "";
  const title = event.title || t("visualizer.untitled");
  const duration = t("schedule.hoursMinutes", { hours: Math.floor(event.duration / 60), minutes: String(event.duration % 60).padStart(2, "0") });
  const platforms = selectedPlatforms(event.platform);
  const shortPlatforms = platforms.join(" + ");
  const shortPlatformPart = shortPlatforms ? ` · ${shortPlatforms}` : "";
  const platformNames = platforms.map(platformName).join(" + ");
  const platformNamePart = platformNames ? ` · ${platformNames}` : "";
  const detail = `${referencePrefix}${event.time}${platformNamePart} · ${title}${event.subheading ? ` · ${event.subheading}` : ""} · ${duration}${event.continued ? ` · ${t("visualizer.continued")}` : ""}`;
  const tone = platformTones[event.platform];
  const cardTone = reference ? "border-dashed bg-white" : tone.card; // Another streamer: dashed, colored per streamer
  const referenceStyle = reference ? { color: reference.color, borderColor: reference.color } : {};
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" tabIndex={tabIndex} aria-label={detail}
          onPointerEnter={event => { if (event.pointerType !== "touch") show(); }}
          onPointerLeave={event => { if (event.pointerType !== "touch") hideSoon(); }}
          onFocus={show} onBlur={hideSoon}
          className={cn("absolute flex flex-col items-stretch justify-start overflow-hidden rounded-sm border px-1 text-left text-[9px] leading-[10px] focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-neutral-900", cardTone)}
          style={{ ...referenceStyle, top: event.start / 60 * HOUR_HEIGHT, height: (event.end - event.start) / 60 * HOUR_HEIGHT, left: `calc(${event.lane / event.lanes * 100}% + 2px)`, width: `calc(${100 / event.lanes}% - 4px)` }}>
          <span className="block shrink-0 truncate">{referencePrefix}<span className="font-mono font-semibold">{event.time}</span>{shortPlatformPart} · {title}</span>
          {(event.end - event.start) >= 90 && <span className="block shrink-0 truncate font-medium">{title}</span>}
          {(event.end - event.start) >= 120 && <span className="block shrink-0 truncate font-mono">{duration}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" updatePositionStrategy="always"
        onPointerEnter={event => { if (event.pointerType !== "touch") show(); }}
        onPointerLeave={event => { if (event.pointerType !== "touch") hideSoon(); }}
        onFocusCapture={show} onBlurCapture={hideSoon} onOpenAutoFocus={event => event.preventDefault()} onCloseAutoFocus={event => event.preventDefault()} className="w-80 max-w-[calc(100vw-2rem)] p-0">
        <SessionDetails event={event} title={title} duration={duration} />
      </PopoverContent>
    </Popover>
  );
}
